<template>
  <div class="auth-page">
    <div class="top-bar">
      <LangSwitch />
    </div>

    <div class="brand">
      <div class="logo"></div>
      <div class="brand-name">Tiempos</div>
    </div>

    <div class="card">
      <div class="card-title">{{ $t('register.submit') }}</div>

      <van-form @submit="onSubmit">
        <div class="field-wrap">
          <div class="field-label">{{ $t('register.user') }}</div>
          <van-field
            v-model="mobile"
            name="mobile"
            type="tel"
            :placeholder="$t('register.userPlaceholder')"
            :rules="[
              { required: true, message: $t('register.userPlaceholder') },
              { pattern: /^\d{8}$/, message: $t('register.userMustBe8Digits'), trigger: 'onBlur' }
            ]"
            :border="false"
          />
        </div>

        <div class="field-wrap">
          <div class="field-label">{{ $t('register.password') }}</div>
          <van-field
            v-model="password"
            type="password"
            name="password"
            :placeholder="$t('register.passwordPlaceholder')"
            :rules="[{ required: true, message: $t('register.passwordPlaceholder') }]"
            :border="false"
          />
        </div>

        <div class="field-wrap">
          <div class="field-label">{{ $t('register.confirmPassword') }}</div>
          <van-field
            v-model="confirmPassword"
            type="password"
            name="confirmPassword"
            :placeholder="$t('register.confirmPasswordPlaceholder')"
            :rules="[{ required: true, message: $t('register.confirmPasswordRequired') }]"
            :border="false"
          />
        </div>

        <div class="field-wrap">
          <div class="field-label">{{ $t('register.name') }}</div>
          <van-field
            v-model="idCard"
            name="idCard"
            :placeholder="$t('register.namePlaceholder')"
            :rules="[{ required: true, message: $t('register.namePlaceholder') }]"
            :border="false"
          />
        </div>

        <div class="field-wrap">
          <div class="field-label">{{ $t('register.sinpe') }}</div>
          <van-field
            v-model="bankNo"
            name="bankNo"
            :placeholder="$t('register.sinpePlaceholder')"
            :rules="[{ required: true, message: $t('register.sinpeRequired') }]"
            :border="false"
          />
        </div>

        <van-button round block native-type="submit" class="submit-btn" :loading="loading"> {{ $t('register.submit') }} </van-button>
      </van-form>

      <div class="footer-link" @click="login">
        {{ $t('register.backToLogin') }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { Toast } from 'vant';
import { RegisterService } from '@/services';
import { useI18n } from 'vue-i18n';
import LangSwitch from '@/components/LangSwitch.vue';
const { t } = useI18n();

const router = useRouter();
const mobile = ref('');
const idCard = ref('');
const bankNo = ref('');
const password = ref('');
const confirmPassword = ref('');
const loading = ref(false);

// Toast 冷却:2 秒内最多弹一次,避免用户长按/连按时刷屏
let lastToastAt = 0;
const showMaxDigitToast = () => {
  const now = Date.now();
  if (now - lastToastAt > 2000) {
    Toast.fail(t('register.userMustBe8Digits'));
    lastToastAt = now;
  }
};

// 监听 mobile 变化:去非数字 + 截 8 位,超 8 位马上弹提示
// (Vant 3 的 van-field 不触发原生 input 事件,只能通过 watch v-model 的 ref 实现)
watch(mobile, (val) => {
  const raw = String(val || '').replace(/\D/g, '');
  const truncated = raw.slice(0, 8);
  if (truncated !== val) mobile.value = truncated;
  if (raw.length > 8) showMaxDigitToast();
});

const onSubmit = async (values) => {
  const uuid = window.location.hash.split('=')[1] ?? '';
  try {
    // 8 位数字二次校验(van-form 已经做了,这里防用户绕过前端)
    if (!/^\d{8}$/.test(values.mobile || '')) {
      Toast.fail(t('register.userMustBe8Digits'));
      return;
    }
    if (values.password != values.confirmPassword) {
      Toast.fail(t('register.passwordMismatch'));
      return;
    }
    loading.value = true;
    const { result } = await RegisterService.registerApi({ ...values, uuid });
    loading.value = false;
    if (result) {
      Toast.success(t('register.success'));
      router.push('/login');
    } else {
      Toast.fail(t('register.fail'));
    }
  } catch (e) {
    console.log('注册失败');
    loading.value = false;
  }
};

const login = () => {
  router.push('/login');
};
</script>

<style lang="scss" scoped>
.auth-page {
  min-height: 100vh;
  background: linear-gradient(160deg, #667eea 0%, #764ba2 100%);
  padding: 40px 32px 60px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
}

.auth-page::before,
.auth-page::after {
  content: '';
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  pointer-events: none;
}
.auth-page::before {
  width: 400px;
  height: 400px;
  top: -140px;
  right: -80px;
}
.auth-page::after {
  width: 300px;
  height: 300px;
  bottom: -100px;
  left: -60px;
}

.top-bar {
  position: absolute;
  top: 30px;
  right: 30px;
  z-index: 2;
  :deep(.lang-text) {
    color: #fff;
  }
  :deep(.lang-icon) {
    color: #fff;
  }
}

.brand {
  text-align: center;
  margin: 40px 0 32px;
  z-index: 1;
  .logo {
    width: 110px;
    height: 110px;
    background-image: url('./../../assets/images/logo.png');
    background-size: cover;
    background-position: center;
    border-radius: 26px;
    margin: 0 auto 18px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }
  .brand-name {
    font-size: 36px;
    font-weight: 700;
    color: #fff;
    letter-spacing: 2px;
  }
}

.card {
  width: 100%;
  background: #fff;
  border-radius: 24px;
  padding: 44px 32px 32px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
  z-index: 1;

  .card-title {
    font-size: 40px;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 28px;
    text-align: center;
    letter-spacing: 2px;
  }
}

.field-wrap {
  margin-bottom: 18px;
  .field-label {
    font-size: 24px;
    color: #6b7280;
    margin-bottom: 8px;
    font-weight: 500;
  }
  :deep(.van-cell) {
    background: #f5f7fa;
    border-radius: 14px;
    padding: 20px 24px;
    font-size: 28px;
    &::after {
      display: none;
    }
  }
  :deep(.van-field__control) {
    color: #1f2937;
    font-size: 28px;
    &::placeholder {
      color: #a0aec0;
    }
  }
}

.submit-btn {
  height: 88px;
  margin-top: 28px;
  background: linear-gradient(135deg, #667eea, #764ba2) !important;
  color: #fff !important;
  border: none !important;
  font-size: 30px !important;
  font-weight: 600;
  letter-spacing: 4px;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.35);
}

.footer-link {
  text-align: center;
  margin-top: 24px;
  color: #667eea;
  font-size: 24px;
  font-weight: 500;
  padding: 12px;
  cursor: pointer;
}
</style>
